/**
 * Canonical Schema.org @id values for BizRnR entities.
 *
 * Every JSON-LD emitter must reference these by { "@id": ORG_ID } rather
 * than re-declaring the entity, so Google / AI crawlers merge duplicates
 * across pages into one coherent knowledge graph.
 *
 * PR B: consolidated here from ad-hoc consts that were scattered across
 * JsonLd.tsx and HomepageGraphJsonLd.tsx.
 */

export const SITE_BASE = 'https://bizrnr.com';

export const ORG_ID = `${SITE_BASE}/#organization`;
export const SITE_ID = `${SITE_BASE}/#website`;
export const SOFTWARE_ID = `${SITE_BASE}/#software`;
export const FOUNDER_ID = `${SITE_BASE}/#founder-kristian-peter`;
export const LOCAL_BIZ_ID = `${SITE_BASE}/#local-business`;
export const GBP_MAPS_URL = 'https://www.google.com/maps/place/BizRnR/data=!4m2!3m1!1s0x0:0xa7500d60b4141c38';
export const GBP_CID = '12056150911460056120';

/** Entity ID helpers for per-route nodes. */
export const webPageId = (path: string): string =>
  `${SITE_BASE}${path.startsWith('/') ? path : `/${path}`}#webpage`;

export const breadcrumbId = (path: string): string =>
  `${SITE_BASE}${path.startsWith('/') ? path : `/${path}`}#breadcrumbs`;

export const faqId = (path: string): string =>
  `${SITE_BASE}${path.startsWith('/') ? path : `/${path}`}#faq`;

export const howToId = (path: string): string =>
  `${SITE_BASE}${path.startsWith('/') ? path : `/${path}`}#howto`;

export const articleId = (path: string): string =>
  `${SITE_BASE}${path.startsWith('/') ? path : `/${path}`}#article`;

export const productId = (path: string): string =>
  `${SITE_BASE}${path.startsWith('/') ? path : `/${path}`}#product`;

/**
 * Absolute URL for any pathname under bizrnr.com. Use in JSON-LD `url`,
 * `sameAs`, and image fields so crawlers never see a relative reference.
 */
export const abs = (path: string): string => {
  if (path.startsWith('http')) return path;
  return `${SITE_BASE}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Organization.sameAs — consolidated list of BizRnR's external profiles.
 * AI assistants (ChatGPT, Perplexity, Claude, Gemini) use these to
 * cross-reference and verify brand identity.
 *
 * Add a new URL here the moment a profile is claimed — a stale sameAs
 * pointing to a broken URL is worse than no entry.
 */
export const ORG_SAME_AS: readonly string[] = [
  'https://x.com/bizrnr',
  'https://youtube.com/@bizrnrai',
  'https://instagram.com/bizrnr',
  'https://pressroom.prlog.org/BizRnR/',
  'https://www.prlog.org/13151499-bizrnr-launches-ai-receptionist-that-answers-every-small-business-call-247.html',
  // Add Crunchbase, LinkedIn, G2, Capterra, Product Hunt, and Wikidata here
  // only after each profile is live and verified.
];
